import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import placeholder from "@/assets/placeholder.png?url";
import { cn } from "@/lib/utils";
import { z } from "zod";
import { ArrowLeft, MessageSquare, SendHorizontal, FileText } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const MESSAGE_TEMPLATES: { label: string; text: string }[] = [
  {
    label: "Sådan får du adgang til pladsen",
    text:
`Sådan får du adgang til pladsen:

1. Adresse: [indsæt adresse + evt. plads-nr.]
2. Adgang: [nøgle / kode / åben port / p-tilladelse]
3. Parkér på den markerede plads – ikke naboens.
4. Skriv til mig her hvis noget driller.

God tur! 🚗`,
  },
  {
    label: "Bekræftelse af booking",
    text:
`Hej! Tak for din booking 🙌

Jeg har bekræftet pladsen fra [start] til [slut].
Du får adgangsinfo herinde senest dagen før.

Skriv gerne hvis du har spørgsmål.`,
  },
  {
    label: "Spørg om ankomsttidspunkt",
    text: "Hej! Ved du cirka hvornår du ankommer, så jeg kan være klar med adgangen?",
  },
];

const messageSchema = z.object({
  text: z.string().trim().min(1, "Beskeden kan ikke være tom").max(5000, "Beskeden er for lang (max 5000 tegn)")
});

type Message = {
  id: string;
  conversation_id: string;
  sender_id: string;
  text: string;
  created_at: string;
  read_at?: string | null;
  read_by?: string[] | null;
};

type Conversation = {
  id: string;
  listing_id: string;
  renter_id: string;
  host_id: string;
  type: "inquiry" | "booking";
  booking_id?: string;
  last_message_at: string;
  created_at: string;
};

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  listingId: string;
  listingTitle: string;
  hostId: string;
  hostName?: string;
  hostAvatarUrl?: string;
  currentUserId: string;
  bookingId?: string; // Optional - if this is a booking chat
  conversationId?: string; // If provided, we load this conversation directly
  renterId?: string; // Explicit renter id (needed when viewer is host)
};

export default function GeneralChatDrawer({
  open,
  onOpenChange,
  listingId,
  listingTitle,
  hostId,
  hostName,
  hostAvatarUrl,
  currentUserId,
  bookingId,
  conversationId,
  renterId,
}: Props): JSX.Element {
  const { toast } = useToast();
  const [messages, setMessages] = useState<Message[]>([]);
  const [text, setText] = useState("");
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [loading, setLoading] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll when new messages arrive
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  // Flyt fokus ind i drawer når den åbner (vent et tick til portalen er mountet)
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  // Load or create conversation
  useEffect(() => {
    if (!open || !currentUserId) {
      return;
    }
    
    const loadConversation = async () => {
      setLoading(true);
      
      try {
        // Verify auth session
        const { data: { user: authUser } } = await supabase.auth.getUser();
        
        if (!authUser || authUser.id !== currentUserId) {
          throw new Error("Auth session mismatch - du skal måske logge ind igen");
        }

        let convId = conversationId || null;
        const conversationType = bookingId ? "booking" : "inquiry";
        const effectiveRenterId = renterId || currentUserId;

        if (!convId) {
          // Find seneste matchende samtale (håndterer også dubletter)
          let query = supabase
            .from("conversations")
            .select("*")
            .eq("listing_id", listingId)
            .eq("host_id", hostId)
            .eq("type", conversationType);
          
          if (bookingId) {
            query = query.eq("booking_id", bookingId);
          } else {
            query = query.is("booking_id", null).eq("renter_id", effectiveRenterId);
          }

          const { data: rows, error: convError } = await query
            .order("created_at", { ascending: false })
            .limit(1);

          if (convError && convError.code !== "PGRST116") {
            throw convError;
          }

          if (rows && rows.length > 0) {
            convId = rows[0].id as string;
          } else {
            // Undgå self-chat
            if (effectiveRenterId === hostId) {
              throw new Error("Kan ikke oprette samtale med dig selv.");
            }

            const { data: newRows, error: createError } = await supabase
              .from("conversations")
              .insert({
                listing_id: listingId,
                renter_id: effectiveRenterId,
                host_id: hostId,
                type: conversationType,
                booking_id: bookingId || null,
              })
              .select()
              .limit(1);

            if (createError) throw createError;
            convId = (newRows?.[0] as any)?.id || null;
          }
        }

        if (!convId) throw new Error("Kunne ikke finde eller oprette samtale");

        setConversation({
          id: convId,
          listing_id: listingId,
          renter_id: effectiveRenterId,
          host_id: hostId,
          type: conversationType as any,
          booking_id: (bookingId || undefined) as any,
          last_message_at: new Date().toISOString(),
          created_at: new Date().toISOString(),
        } as any);

        // Load messages for this conversation
        const { data: messagesData, error: messagesError } = await supabase
          .from("messages")
          .select("*")
          .eq("conversation_id", convId)
          .order("created_at", { ascending: true });

        if (messagesError) throw messagesError;
        setMessages(messagesData as Message[]);
      } catch (error: any) {
        console.error("Chat drawer error:", error);
        toast({
          title: "Fejl ved indlæsning af chat",
          description: error.message || "Kunne ikke indlæse samtalen. Prøv at genindlæse siden.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadConversation();
  }, [open, currentUserId, listingId, hostId, bookingId, conversationId, renterId]);

  // Real-time message updates
  useEffect(() => {
    if (!conversation) return;

    const channel = supabase
      .channel(`conversation-${conversation.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `conversation_id=eq.${conversation.id}`,
        },
        (payload) => {
          setMessages((prev) => [...prev, payload.new as Message]);
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "messages",
          filter: `conversation_id=eq.${conversation.id}`,
        },
        (payload) => {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === payload.new.id ? (payload.new as Message) : msg
            )
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversation]);

  // Mark messages as read when viewing
  useEffect(() => {
    if (!conversation || !open || messages.length === 0) return;

    const markAsRead = async () => {
      const unreadMessages = messages.filter(
        (msg) => msg.sender_id !== currentUserId && !msg.read_at
      );

      if (unreadMessages.length === 0) return;

      for (const msg of unreadMessages) {
        await supabase
          .from("messages")
          .update({
            read_at: new Date().toISOString(),
            read_by: [currentUserId],
          })
          .eq("id", msg.id);
      }
    };

    // Mark as read after a short delay to ensure user has seen them
    const timer = setTimeout(markAsRead, 1000);
    return () => clearTimeout(timer);
  }, [conversation, open, messages, currentUserId]);

  const sendMessage = async () => {
    const messageText = text.trim();
    if (!messageText) return;

    // Validate message
    try {
      messageSchema.parse({ text: messageText });
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Ugyldig besked",
          description: error.errors[0].message,
          variant: "destructive",
        });
      }
      return;
    }

    if (!conversation) {
      toast({
        title: "Samtale ikke klar",
        description: "Vent et øjeblik mens samtalen indlæses, og prøv igen.",
        variant: "destructive",
      });
      return;
    }

    setText("");

    try {
      // Sikkerhedstjek: bekræft at brugeren er logget ind
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        throw new Error("Du er ikke logget ind. Log ind og prøv igen.");
      }

      const { error } = await supabase
        .from("messages")
        .insert({
          conversation_id: conversation.id,
          sender_id: currentUserId,
          text: messageText,
          booking_id: conversation.booking_id || null,
        });

      if (error) throw error;
    } catch (error: any) {
      console.error("Error sending message:", error);
      toast({
        title: "Kunne ikke sende besked",
        description: error?.message || "Ukendt fejl. Prøv igen.",
        variant: "destructive",
      });
      setText(messageText); // Restore text on error
    }
  };

  const isMyMessage = (message: Message) => message.sender_id === currentUserId;

  const getMessageStatus = (message: Message) => {
    if (!isMyMessage(message)) return null;
    if (message.read_at) return "Læst";
    return "Leveret";
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent className="chat-drawer fixed inset-0 left-0 top-0 h-[100dvh] w-screen max-w-none translate-x-0 translate-y-0 rounded-none border-0 p-0">
        {/* Header */}
        <DialogHeader className="chat-header">
          <DialogTitle className="sr-only">
            Chat med {hostName || "Værten"} om {listingTitle}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Samtale om parkeringsplads
          </DialogDescription>
          <div className="flex items-center gap-3 w-full">
            <button
              type="button"
              className="chat-back-button"
              onClick={() => onOpenChange(false)}
              aria-label="Luk samtale"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <img 
              src={hostAvatarUrl || placeholder}
              alt={hostName || "Vært"}
              className="avatar-image"
              onError={(e) => {
                if (e.currentTarget.src !== placeholder)
                  e.currentTarget.src = placeholder;
              }}
            />
            <div className="min-w-0 flex-1">
              <h2>{hostName || "Værten"}</h2>
              <p>{listingTitle}</p>
            </div>
            <span className="chat-status-pill">Samtale</span>
          </div>
        </DialogHeader>

        {/* Messages */}
        <div className="chat-scroll">
          <div className="chat-messages-area">
            {loading ? (
              <div className="chat-empty-state">
                Indlæser samtale...
              </div>
            ) : messages.length === 0 ? (
              <div className="chat-empty-state">
                <MessageSquareIcon />
                <p>Start samtalen om parkeringspladsen</p>
              </div>
            ) : (
              messages.map((message, idx) => {
                const isMe = isMyMessage(message);
                const prevMessage = idx > 0 ? messages[idx - 1] : null;
                const nextMessage = idx < messages.length - 1 ? messages[idx + 1] : null;
                const showAvatar = !isMe && (!prevMessage || isMyMessage(prevMessage));
                const showStatus = isMe && (!nextMessage || !isMyMessage(nextMessage));
                const status = getMessageStatus(message);
                
                return (
                  <div key={message.id} className={cn("message-stack", isMe ? "message-stack--mine" : "message-stack--other")}>
                    <div
                      className={cn(
                        "message-row",
                        isMe ? "message-row--mine" : "message-row--other",
                        !showAvatar && "message-row--same-sender"
                      )}
                    >
                      {!isMe && (
                        <img 
                          src={hostAvatarUrl || placeholder}
                          alt={hostName || "Vært"}
                          className="message-avatar"
                          onError={(e) => {
                            if (e.currentTarget.src !== placeholder)
                              e.currentTarget.src = placeholder;
                          }}
                        />
                      )}
                      <div className={cn(
                        "message-bubble",
                        isMe ? "message-bubble--mine" : "message-bubble--other"
                      )}>
                        <p>{message.text}</p>
                      </div>
                    </div>
                    {showStatus && status && <div className="message-status">{status}</div>}
                  </div>
                );
              })
            )}
            <div ref={endRef} />
          </div>
        </div>

        {/* Input */}
        <div className="chat-input-area">
          <div className="flex items-end gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="shrink-0 inline-flex items-center justify-center h-10 w-10 rounded-full border border-border bg-background text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  aria-label="Indsæt skabelon"
                  title="Indsæt skabelon"
                >
                  <FileText className="h-5 w-5" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" side="top" className="w-72 p-1">
                <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                  Hurtige skabeloner
                </div>
                {MESSAGE_TEMPLATES.map((tpl) => (
                  <button
                    key={tpl.label}
                    type="button"
                    className="w-full text-left px-2 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                    onClick={() => {
                      setText((prev) => (prev ? `${prev}\n\n${tpl.text}` : tpl.text));
                      setTimeout(() => inputRef.current?.focus(), 0);
                    }}
                  >
                    {tpl.label}
                  </button>
                ))}
              </PopoverContent>
            </Popover>
            <textarea
              ref={inputRef}
              placeholder="Skriv din besked... (Shift+Enter = ny linje)"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              disabled={loading || !conversation}
              rows={1}
            />
            <button 
              onClick={sendMessage} 
              disabled={!text.trim() || loading || !conversation}
              aria-label="Send besked"
            >
              <SendHorizontal className="h-5 w-5" />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MessageSquareIcon() {
  return <MessageSquare className="chat-empty-icon" />;
}